<template>
  <div class="imp-device">
    <!-- Instagram Top Header Bar -->
    <div class="imp-top-bar">
      <span class="imp-username imp-clickable" @click="openInstagramAccount" :title="$t('dash.presenceManagement.instagram.openAccount')">
        {{ usernameDisplay }}
      </span>
      <i class="material-icons imp-share-btn" @click="shareAccount" :title="$t('dash.presenceManagement.mockup.share')">share</i>
    </div>

    <!-- Profile Content Screen -->
    <div class="imp-screen">

      <!-- Header: Avatar + Stats (Abonnés, Suivi(e)s) -->
      <div class="imp-header">
        <div class="imp-avatar-ring imp-clickable" @click="openInstagramAccount" :title="$t('dash.presenceManagement.instagram.openAccount')">
          <div 
            class="imp-avatar"
            :style="imageUrl ? `background-image: url('${imageUrl}')` : ''"
          >
            <i v-if="!imageUrl" class="material-icons imp-avatar-placeholder">person</i>
          </div>
        </div>

        <div class="imp-stats">
          <div class="imp-stat">
            <span class="imp-stat-num">{{ followersDisplay }}</span>
            <span class="imp-stat-label">{{ $t('dash.presenceManagement.instagram.followers') }}</span>
          </div>
          <div class="imp-stat">
            <span class="imp-stat-num">{{ followsDisplay }}</span>
            <span class="imp-stat-label">{{ $t('dash.presenceManagement.instagram.following') }}</span>
          </div>
        </div>
      </div>

      <!-- Bio / Info Section -->
      <div class="imp-bio-section">
        <h4 class="imp-fullname imp-clickable" @click="openInstagramAccount" :title="$t('dash.presenceManagement.instagram.openAccount')">
          {{ name || usernameDisplay }}
        </h4>
        <span class="imp-category-tag">{{ categoryName || $t('dash.presenceManagement.instagram.defaultCategory') }}</span>
        <p class="imp-bio-text">{{ biography || $t('dash.presenceManagement.instagram.noBio') }}</p>

        <a v-if="website" :href="targetWebsiteUrl" target="_blank" class="imp-website-link">
          <i class="material-icons">link</i>
          <span>{{ cleanWebsite }}</span>
        </a>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'instagram-mockup-preview',

  props: {
    name: { type: String, default: '' },
    username: { type: String, default: '' },
    website: { type: String, default: '' },
    phone: { type: String, default: '' },
    email: { type: String, default: '' },
    categoryName: { type: String, default: '' },
    imageUrl: { type: String, default: '' },
    coverImageUrl: { type: String, default: '' },
    biography: { type: String, default: '' },
    followersCount: { type: [Number, String], default: null },
    followsCount: { type: [Number, String], default: null },
    instagramUri: { type: String, default: '' },
    additionalPhotos: { type: Array, default: () => [] }
  },

  computed: {
    usernameDisplay() {
      if (this.username) {
        return this.username.startsWith('@') ? this.username : `@${this.username}`
      }
      if (this.name) {
        return `@${this.name.toLowerCase().replace(/[^a-z0-9_.]/g, '')}`
      }
      return '@mon_etablissement'
    },

    cleanUsername() {
      return this.usernameDisplay.replace(/^@/, '')
    },

    instagramUrl() {
      if (this.instagramUri) return this.instagramUri
      if (this.cleanUsername && this.cleanUsername !== 'mon_etablissement') {
        return `https://instagram.com/${this.cleanUsername}`
      }
      return 'https://instagram.com'
    },

    cleanWebsite() {
      if (!this.website) return ''
      return this.website.replace(/^https?:\/\//i, '').replace(/\/$/, '')
    },

    targetWebsiteUrl() {
      if (!this.website) return '#'
      return this.website.startsWith('http') ? this.website : `https://${this.website}`
    },

    followersDisplay() {
      if (this.followersCount !== null && this.followersCount !== undefined) {
        const num = parseInt(this.followersCount, 10)
        if (!isNaN(num)) {
          return num >= 1000 ? `${(num / 1000).toFixed(1)}k` : String(num)
        }
      }
      return '0'
    },

    followsDisplay() {
      if (this.followsCount !== null && this.followsCount !== undefined) {
        const num = parseInt(this.followsCount, 10)
        if (!isNaN(num)) {
          return num >= 1000 ? `${(num / 1000).toFixed(1)}k` : String(num)
        }
      }
      return '0'
    }
  },

  methods: {
    openInstagramAccount() {
      window.open(this.instagramUrl, '_blank')
    },

    shareAccount() {
      const shareUrl = this.instagramUrl || this.website || window.location.href
      if (navigator.share) {
        navigator.share({ title: this.name || this.usernameDisplay, url: shareUrl }).catch(() => {})
      } else {
        window.open(shareUrl, '_blank')
      }
    }
  }
}
</script>

<style scoped>
.imp-device {
  background: #e8e8e8;
  border-radius: 36px;
  padding: 14px 10px;
  width: 260px;
  flex-shrink: 0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08), inset 0 0 0 2px #dadada;
  margin: 0 auto;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  user-select: none;
}

/* ─── Top Bar ─── */
.imp-top-bar {
  height: 42px;
  background: #ffffff;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  border-bottom: 1px solid #efefef;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px;
  box-sizing: border-box;
}

.imp-username {
  font-size: 13px;
  font-weight: 700;
  color: #262626;
  cursor: pointer;
  transition: color 0.15s;
}

.imp-username:hover {
  color: #3897f0;
}

.imp-share-btn {
  font-size: 18px;
  color: #262626;
  cursor: pointer;
  opacity: 0.8;
  transition: opacity 0.15s;
}

.imp-share-btn:hover {
  opacity: 1;
}

.imp-clickable {
  cursor: pointer;
}

/* ─── Screen ─── */
.imp-screen {
  background: #ffffff;
  border-bottom-left-radius: 24px;
  border-bottom-right-radius: 24px;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 14px 12px;
  box-sizing: border-box;
}

/* ─── Header: Avatar + Stats ─── */
.imp-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 14px;
}

.imp-avatar-ring {
  padding: 2px;
  background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);
  border-radius: 50%;
  flex-shrink: 0;
  cursor: pointer;
}

.imp-avatar {
  width: 58px;
  height: 58px;
  border-radius: 50%;
  border: 2px solid #fff;
  background-color: #fafafa;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.imp-avatar-placeholder {
  font-size: 32px;
  color: #dbdbdb;
}

.imp-stats {
  display: flex;
  flex: 1;
  justify-content: space-around;
  text-align: center;
}

.imp-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.imp-stat-num {
  font-size: 14px;
  font-weight: 700;
  color: #262626;
}

.imp-stat-label {
  font-size: 11px;
  color: #8e8e8e;
}

/* ─── Bio Section ─── */
.imp-bio-section {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.imp-fullname {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #262626;
  line-height: 1.3;
  cursor: pointer;
  transition: color 0.15s;
}

.imp-fullname:hover {
  color: #3897f0;
}

.imp-category-tag {
  font-size: 11px;
  color: #8e8e8e;
  margin-bottom: 2px;
}

.imp-bio-text {
  margin: 4px 0 8px;
  font-size: 11px;
  line-height: 1.4;
  color: #262626;
  white-space: pre-line;
  word-break: break-word;
}

.imp-website-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #00376b;
  text-decoration: none;
  word-break: break-all;
}

.imp-website-link i {
  font-size: 13px;
}

.imp-website-link:hover {
  text-decoration: underline;
}
</style>
